import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CpmButton, CpmStackedProgressBar } from '../../../components/design-system';
import {
    CPM_STATUS_BAR_COLORS,
    formatCpmCount,
} from '../../../constants/centralProductMaster.constants';
import type { CpmStatusCountMap } from '../../../types/centralProductMaster.types';

type StatusKey = 'PENDING' | 'ACTIVE' | 'NEEDS_REVIEW' | 'MERGED';

const ROWS: Array<{
    key: StatusKey;
    label: string;
    warn?: boolean;
    muted?: boolean;
}> = [
    { key: 'PENDING', label: 'Pending review' },
    { key: 'ACTIVE', label: 'Active / verified' },
    { key: 'NEEDS_REVIEW', label: 'Needs review', warn: true },
    { key: 'MERGED', label: 'Merged', muted: true },
];

interface CatalogHealthCardProps {
    title: string;
    icon: ReactNode;
    data: CpmStatusCountMap;
    editorHref: (status: StatusKey) => string;
    error?: boolean;
    onRetry?: () => void;
    retrying?: boolean;
    errorLabel?: string;
}

export default function CatalogHealthCard({
    title,
    icon,
    data,
    editorHref,
    error = false,
    onRetry,
    retrying = false,
    errorLabel,
}: CatalogHealthCardProps) {
    const by = data.byStatus ?? {};
    const segments = ROWS.map((r) => ({
        key: r.key,
        value: by[r.key] ?? 0,
        color: CPM_STATUS_BAR_COLORS[r.key],
    }));
    const lower = title.toLowerCase();

    return (
        <div
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col"
            style={{ padding: 20, gap: 16 }}
        >
            <div className="flex items-center" style={{ gap: 10 }}>
                <span
                    className="flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                    style={{ width: 30, height: 30, borderRadius: 8 }}
                >
                    {icon}
                </span>
                <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>
                    {title}
                </span>
                <span
                    className="ml-auto uppercase text-[var(--cpm-text-tertiary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em' }}
                >
                    Master
                </span>
            </div>
            {error ? (
                <div className="flex flex-col" style={{ gap: 12, padding: '8px 0 4px' }}>
                    <span
                        className="text-[var(--cpm-shortfall-text)]"
                        style={{ fontSize: 13.5, fontWeight: 800 }}
                    >
                        {errorLabel ?? `Couldn't load ${lower} counts`}
                    </span>
                    <span
                        className="text-[var(--cpm-shortfall-text-med)]"
                        style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        This aggregate did not return. Nothing has changed — retry to load the
                        breakdown.
                    </span>
                    {onRetry ? (
                        <CpmButton
                            variant="outline"
                            size="sm"
                            className="!self-start"
                            disabled={retrying}
                            leadingIcon={retrying ? <span className="cpm-spin" /> : undefined}
                            onClick={onRetry}
                        >
                            {retrying ? 'Retrying…' : 'Retry'}
                        </CpmButton>
                    ) : null}
                </div>
            ) : (
                <>
                    <div className="flex items-baseline" style={{ gap: 8 }}>
                        <span
                            className="tabular"
                            style={{
                                fontSize: 38,
                                fontWeight: 800,
                                letterSpacing: '-0.025em',
                                lineHeight: 1,
                            }}
                        >
                            {formatCpmCount(data.total)}
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)]"
                            style={{ fontSize: 12.5, fontWeight: 600 }}
                        >
                            total records
                        </span>
                    </div>
                    <CpmStackedProgressBar segments={segments} height={8} />
                    <div className="flex flex-col" style={{ gap: 2 }}>
                        {ROWS.map((row, i) => {
                            const count = by[row.key] ?? 0;
                            const isLast = i === ROWS.length - 1;
                            return (
                                <Link
                                    key={row.key}
                                    to={editorHref(row.key)}
                                    className={[
                                        'flex items-center no-underline !text-inherit',
                                        isLast ? '' : 'border-b border-[var(--cpm-border-default)]',
                                    ].join(' ')}
                                    style={{ gap: 10, padding: '7px 0' }}
                                >
                                    <span
                                        className="shrink-0 rounded-full"
                                        style={{
                                            width: 9,
                                            height: 9,
                                            background: CPM_STATUS_BAR_COLORS[row.key],
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: row.warn
                                                ? 'var(--cpm-warning-text)'
                                                : row.muted
                                                  ? 'var(--cpm-text-secondary)'
                                                  : undefined,
                                        }}
                                    >
                                        {row.label}
                                    </span>
                                    <span
                                        className="font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-tertiary)]"
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        {row.key}
                                    </span>
                                    <span
                                        className="ml-auto tabular"
                                        style={{
                                            fontSize: 14.5,
                                            fontWeight: 800,
                                            color: row.warn
                                                ? 'var(--cpm-warning-text)'
                                                : row.muted
                                                  ? 'var(--cpm-text-secondary)'
                                                  : undefined,
                                        }}
                                    >
                                        {formatCpmCount(count)}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
